import { useMemo, useState } from "react";
import { CheckCircle2, KeyRound, LockKeyhole } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { resetPassword } from "../api/auth";
import {
  getPasswordConfirmationError,
  getPasswordError,
  normalizeApiError,
} from "../utils/authValidation";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:border-indigo-300 focus:outline-none focus:ring-4 focus:ring-indigo-200/60";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(token ? "" : "Ссылка сброса пароля некорректна");
  const [success, setSuccess] = useState(false);

  const passwordError = useMemo(() => getPasswordError(password), [password]);
  const confirmationError = useMemo(
    () => getPasswordConfirmationError(password, confirmation),
    [password, confirmation],
  );

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError("");

    if (!token) {
      setError("Ссылка сброса пароля некорректна");
      return;
    }
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (confirmationError) {
      setError(confirmationError);
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(normalizeApiError(err, "Не удалось установить новый пароль"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white px-6 py-10 text-slate-900">
      <main className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-xl items-center justify-center">
        <section className="w-full rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
            {success ? <CheckCircle2 size={28} /> : <LockKeyhole size={28} />}
          </div>
          <h1 className="mt-5 text-2xl font-bold text-slate-900">Новый пароль</h1>

          {success ? (
            <div className="mt-5 space-y-5">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                Пароль обновлён. Теперь можно войти с новым паролем.
              </div>
              <Link
                to="/auth/login"
                className="inline-flex w-full items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Ко входу
              </Link>
            </div>
          ) : (
            <>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Установите пароль не короче 8 символов, с цифрой и заглавной буквой.
              </p>

              {error && (
                <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <form onSubmit={onSubmit} className="mt-6 space-y-5">
                <label className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">Пароль</span>
                  <input
                    className={inputClass}
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  {password && passwordError && (
                    <span className="block text-xs text-amber-700">{passwordError}</span>
                  )}
                </label>

                <label className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">Подтверждение пароля</span>
                  <input
                    className={inputClass}
                    type="password"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  {confirmation && confirmationError && (
                    <span className="block text-xs text-amber-700">{confirmationError}</span>
                  )}
                </label>

                <button
                  type="submit"
                  disabled={submitting || !token}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(79,70,229,0.25)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50"
                >
                  <KeyRound size={18} />
                  {submitting ? "Сохраняем..." : "Сохранить пароль"}
                </button>
              </form>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
